// src/components/BatteryOptimizationPrompt.js
//
// Modal shown on Android to explain OEM battery-saver policies and offer a
// direct link to the device's Settings so the user can whitelist SleepMixBlueprint.
//
// Usage:
//   <BatteryOptimizationPrompt
//     visible={showPrompt}
//     onDismiss={() => setShowPrompt(false)}
//     mode="reactive"   // 'reactive' (audio already stopped) | 'manual' (user-initiated)
//   />

import React from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Linking,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Radius, Fonts } from '../theme';

// Opens the most specific battery-settings screen we can reach without a
// native plugin. On Android we try the REQUEST_IGNORE_BATTERY_OPTIMIZATIONS
// intent first (works on stock Android and many OEMs); if that fails we fall
// back to the plain App Settings page.
// Exported for unit testing.
export async function openBatterySettings() {
  if (Platform.OS !== 'android') return;

  // The android-app intent scheme lets us launch any exported activity by
  // action name. ACTION_REQUEST_IGNORE_BATTERY_OPTIMIZATIONS shows the system
  // dialog that lets the user add our package to the "unrestricted" list.
  // This is the most direct path and works on stock AOSP, Samsung One UI,
  // Xiaomi MIUI, and OnePlus OxygenOS.
  const pkg = 'com.sleepmix.app'; // package name from app.json
  const intentUrl = `intent:#Intent;action=android.settings.REQUEST_IGNORE_BATTERY_OPTIMIZATIONS;data=package:${pkg};end`;

  const canOpen = await Linking.canOpenURL(intentUrl).catch(() => false);
  if (canOpen) {
    await Linking.openURL(intentUrl).catch(() => {});
    return;
  }

  // Fallback: open the generic app-settings page where the user can navigate
  // to Battery > Unrestricted manually.
  await Linking.openSettings().catch(() => {});
}

// Copy variants keyed by mode.
// Exported so unit tests can assert the expected strings without rendering the component.
export const COPY = {
  reactive: {
    icon: 'battery-dead-outline',
    heading: 'Audio stopped in the background',
    body1:
      "Your phone's battery-saver settings interrupted SleepMixBlueprint while the " +
      'screen was off. This can happen on Samsung, Xiaomi, Huawei, and ' +
      'OnePlus devices.',
    body2:
      'To keep audio playing all night, allow SleepMixBlueprint to run without ' +
      'restrictions in your battery settings.',
    step3: 'Restart your mix — audio will stay on all night',
  },
  manual: {
    icon: 'battery-charging-outline',
    heading: 'Keep audio running all night',
    body1:
      'Some Android devices (Samsung, Xiaomi, Huawei, OnePlus) aggressively ' +
      'stop background apps to save battery, which can cut your mix short.',
    body2:
      'Allowing SleepMixBlueprint to run unrestricted takes about ten seconds and ' +
      'prevents audio from stopping while your screen is off.',
    step3: 'Come back and start your mix — it will play all night',
  },
};

export default function BatteryOptimizationPrompt({ visible, onDismiss, mode = 'reactive' }) {
  // This prompt contains Android-specific instructions and links to Android
  // battery settings; it has no meaningful effect on iOS, so render nothing.
  if (Platform.OS !== 'android') return null;

  const copy = COPY[mode] ?? COPY.reactive;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      statusBarTranslucent
      onRequestClose={onDismiss}
    >
      <View style={styles.backdrop}>
        <View style={styles.card}>
          {/* Icon */}
          <View style={styles.iconWrap}>
            <Ionicons name={copy.icon} size={36} color={Colors.warning} />
          </View>

          {/* Heading */}
          <Text style={styles.heading}>{copy.heading}</Text>

          {/* Body */}
          <Text style={styles.body}>{copy.body1}</Text>
          <Text style={styles.body}>{copy.body2}</Text>

          {/* Steps hint */}
          <View style={styles.steps}>
            <Text style={styles.stepText}>
              1. Tap <Text style={styles.emphasis}>Fix this</Text> below
            </Text>
            <Text style={styles.stepText}>
              2. Select <Text style={styles.emphasis}>Unrestricted</Text> or{' '}
              <Text style={styles.emphasis}>Don't optimize</Text>
            </Text>
            <Text style={styles.stepText}>3. {copy.step3}</Text>
          </View>

          {/* Buttons */}
          <TouchableOpacity
            style={styles.primaryBtn}
            onPress={async () => {
              onDismiss();
              await openBatterySettings();
            }}
            activeOpacity={0.8}
          >
            <Ionicons name="settings-outline" size={18} color={Colors.textOnAccent} />
            <Text style={styles.primaryBtnText}>Fix this</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.secondaryBtn}
            onPress={onDismiss}
            activeOpacity={0.7}
          >
            <Text style={styles.secondaryBtnText}>Dismiss</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  card: {
    backgroundColor: Colors.surface,
    borderRadius: Radius.xl,
    padding: 24,
    width: '100%',
    maxWidth: 380,
    borderWidth: 1,
    borderColor: Colors.border,
    alignItems: 'center',
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Colors.surface2,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  heading: {
    color: Colors.textBright,
    fontSize: 17,
    fontFamily: Fonts.semiBold,
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    color: Colors.textSecondary,
    fontSize: 14,
    fontFamily: Fonts.regular,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 10,
  },
  steps: {
    alignSelf: 'stretch',
    backgroundColor: Colors.surface2,
    borderRadius: Radius.md,
    padding: 14,
    marginTop: 4,
    marginBottom: 20,
    gap: 6,
  },
  stepText: {
    color: Colors.textSecondary,
    fontSize: 13,
    fontFamily: Fonts.regular,
    lineHeight: 18,
  },
  emphasis: {
    color: Colors.textPrimary,
    fontFamily: Fonts.semiBold,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: Colors.accent,
    borderRadius: Radius.pill,
    paddingVertical: 13,
    paddingHorizontal: 28,
    alignSelf: 'stretch',
    justifyContent: 'center',
    marginBottom: 10,
  },
  primaryBtnText: {
    color: Colors.textOnAccent,
    fontSize: 15,
    fontFamily: Fonts.bold,
  },
  secondaryBtn: {
    paddingVertical: 10,
    paddingHorizontal: 20,
  },
  secondaryBtnText: {
    color: Colors.textMuted,
    fontSize: 14,
    fontFamily: Fonts.regular,
  },
});
